import React, { useEffect, useState } from 'react';
import { translate } from 'components/Translation';
import { BreadcrumbsItem } from 'react-breadcrumbs-dynamic';
import PaymentService from 'services/payments/payment';
import { IPaymentToken } from 'utils/api/models/payments/paymentTokens';
import { finalize } from 'rxjs/operators';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import { useDispatch } from 'react-redux';
import { hideLoadingIndicator, showLoadingIndicator } from 'redux/modules/api/loadingIndicator/actions';
import CardsList from './CardsList';
import invalidateCache from 'utils/api/invalidateCache';
import SuccessDialog from './subcomponents/SuccessDialog';
import FailureDialog from './subcomponents/FailureDialog';

const Wallet = () => {
	const [paymentTokens, setPaymentsTokens] = useState<IPaymentToken[]>([]);
	const [loading, setLoading] = useState<boolean>(true);
	const [last4Digits, setLast4Digits] = useState<string>('');
	const [showDialog, setShowDialog] = useState<'success' | 'failure' | null>(null);

	const dispatch = useDispatch();

	useEffect(() => {
		setLoading(true);
		dispatch(showLoadingIndicator());
		PaymentService.getPaymentTokens()
			.pipe(
				finalize(() => {
					dispatch(hideLoadingIndicator());
					setLoading(false);
				})
			)
			.subscribe(({ tokens }) => setPaymentsTokens(tokens));
	}, [dispatch]);

	const removeToken = (id: string, last4Digits: string) => {
		dispatch(showLoadingIndicator());
		PaymentService.removePaymentToken(id)
			.pipe(finalize(() => dispatch(hideLoadingIndicator())))
			.subscribe(
				() => {
					invalidateCache(['getPaymentTokensForCurrentUser']);
					setPaymentsTokens((tokens) => tokens.filter((singleToken) => singleToken.id !== id));
					showSuccessDialog(last4Digits);
				},
				() => showFailureDialog()
			);
	};

	const hideDialog = () => {
		setShowDialog(null);
	};

	const showSuccessDialog = (last4Digits: string) => {
		setLast4Digits(last4Digits);
		setShowDialog('success');
	};

	const hideSuccessDialog = () => {
		setLast4Digits('');
		hideDialog();
	};

	const showFailureDialog = () => {
		setShowDialog('failure');
	};

	return (
		<>
			<BreadcrumbsItem to="/payments/payments-history">
				{`${translate('path.my.payments')} - ${translate('path.my.wallet')}`}
			</BreadcrumbsItem>
			<ConditionalRender
				show={!loading}
				onTrue={() => <CardsList paymentTokens={paymentTokens} removeToken={removeToken} />}
			/>
			<SuccessDialog isOpen={showDialog === 'success'} hideDialog={hideSuccessDialog} last4Digits={last4Digits} />
			<FailureDialog isOpen={showDialog === 'failure'} hideDialog={hideDialog} />
		</>
	);
};

export default Wallet;
